import { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Spinner, SpinnerSize, DetailsList } from '@fluentui/react'
import { ResponsiveBar } from '@nivo/bar'

import { getStatistics } from 'actions/statistics'
import { selectStatistics, selectStatisticsLoading } from 'selectors/statistics'
import StatCard from './shared/StatCard'

import './Statistics.scss'

const fmt = (n) => (n != null ? n.toLocaleString() : '—')

const SHORT_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

// Convert "2018-01" to "Jan 2018"
const formatMonth = (ym) => {
  if (!ym || ym.length !== 7) return ym
  const [year, month] = ym.split('-')
  return `${SHORT_MONTHS[parseInt(month, 10) - 1]} ${year}`
}

// ─── Small ranked table helper ───────────────────────────────────────────────

const RankedTable = ({ items, columns }) => (
  <div className="rankedTableWrap">
    <DetailsList
      items={items}
      columns={columns}
      getKey={(_, i) => i}
      setKey="none"
      selectionMode={0}
      checkboxVisibility={2}
      compact
    />
  </div>
)

// ─── Bar chart wrapper ───────────────────────────────────────────────────────

const BarChart = ({ data, indexKey, valueKey, height = 300, formatTick, tickInterval }) => {
  // Compute tick values to show based on interval
  const tickValues = tickInterval
    ? data.filter((_, i) => i % tickInterval === 0).map(d => d[indexKey])
    : undefined

  return (
    <div style={{ height }}>
      <ResponsiveBar
        data={data}
        keys={[valueKey]}
        indexBy={indexKey}
        margin={{ top: 10, right: 20, bottom: 60, left: 55 }}
        padding={0.3}
        minValue={0}
        colors={['#1c1c1c']}
        axisBottom={{
          tickRotation: -45,
          tickSize: 4,
          tickPadding: 4,
          tickValues,
          format: formatTick,
        }}
        axisLeft={{
          tickSize: 4,
          tickPadding: 4,
        }}
        enableLabel={false}
        tooltip={({ indexValue, value }) => (
          <div className="chartTooltip">
            <strong>{formatTick ? formatTick(indexValue) : indexValue}</strong>: {fmt(value)}
          </div>
        )}
      />
    </div>
  )
}

// ─── Main component ──────────────────────────────────────────────────────────

const Statistics = () => {
  const dispatch = useDispatch()
  const stats = useSelector(selectStatistics)
  const isLoading = useSelector(selectStatisticsLoading)

  useEffect(() => {
    dispatch(getStatistics())
  }, [dispatch])

  if (isLoading || !stats) {
    return (
      <div className="statistics">
        <h1>Statistics</h1>
        <div className="loadingState">
          <Spinner size={SpinnerSize.large} label="Loading statistics..." />
        </div>
      </div>
    )
  }

  const byType = stats.by_type || {}

  // Map hash objects to arrays for tables
  const popularEvents = Object.entries(stats.popular_events || {}).map(([name, count], i) => ({
    rank: i + 1, name, count,
  }))

  const mostRestreams = Object.entries(stats.most_restreams || {}).map(([name, count], i) => ({
    rank: i + 1, name, count,
  }))

  const ctm = Object.entries(stats.ctm || {}).map(([tier, count]) => ({ tier, count }))
  const ctl = Object.entries(stats.ctl || {}).map(([div, count]) => ({ div, count }))

  return (
    <div className="statistics">
      <h1>Statistics</h1>

      {/* ── Overall ──────────────────────────────────────────────────── */}
      <div className="statsSection">
        <h2>Overall</h2>
        <div className="statsRow">
          <StatCard title="Total Matches" value={fmt(stats.total)} />
          {Object.entries(byType)
            .sort(([, a], [, b]) => b - a)
            .map(([type, count]) => (
              <StatCard key={type} title={type} value={fmt(count)} />
            ))
          }
        </div>
      </div>

      {/* ── Activity ─────────────────────────────────────────────────── */}
      <div className="statsSection">
        <h2>Activity</h2>
        <div className="statsRow">
          <StatCard title="Matches (Last 365 Days)" value={fmt(stats.last_365_days)} />
          <StatCard title="Avg/Day (Last 365 Days)" value={stats.avg_per_day_last_365} />
          <StatCard title="Matches (Prev 365 Days)" value={fmt(stats.prev_365_days)} />
          <StatCard title="Avg/Day (Prev 365 Days)" value={stats.avg_per_day_prev_365} />
        </div>
      </div>

      {/* ── Popular Events + Most Restreams ───────────────────────── */}
      <div className="statsSplit">
        <div className="statsSection">
          <h2>Popular Events (Top 15)</h2>
          <RankedTable
            items={popularEvents}
            columns={[
              { key: 'rank', name: '#', fieldName: 'rank', minWidth: 30, maxWidth: 30 },
              { key: 'name', name: 'Event', fieldName: 'name', minWidth: 120 },
              { key: 'count', name: 'Matches', minWidth: 60, maxWidth: 80,
                onRender: item => fmt(item.count) },
            ]}
          />
        </div>
        <div className="statsSection">
          <h2>Most Restreams (Top 25)</h2>
          <RankedTable
            items={mostRestreams}
            columns={[
              { key: 'rank', name: '#', fieldName: 'rank', minWidth: 30, maxWidth: 30 },
              { key: 'name', name: 'Restreamer', fieldName: 'name', minWidth: 120 },
              { key: 'count', name: 'Matches', minWidth: 60, maxWidth: 80,
                onRender: item => fmt(item.count) },
            ]}
          />
        </div>
      </div>

      {/* ── CTM + CTL ─────────────────────────────────────────────── */}
      <div className="statsSplit">
        <div className="statsSection">
          <h2>CTM Matches</h2>
          <RankedTable
            items={ctm}
            columns={[
              { key: 'tier', name: 'Tier', fieldName: 'tier', minWidth: 120 },
              { key: 'count', name: 'Matches', minWidth: 60, maxWidth: 80,
                onRender: item => fmt(item.count) },
            ]}
          />
        </div>
        <div className="statsSection">
          <h2>CTL Matches</h2>
          <RankedTable
            items={ctl}
            columns={[
              { key: 'div', name: 'Division', fieldName: 'div', minWidth: 120 },
              { key: 'count', name: 'Matches', minWidth: 60, maxWidth: 80,
                onRender: item => fmt(item.count) },
            ]}
          />
        </div>
      </div>

      {/* ── Player Leaderboards ───────────────────────────────────── */}
      <div className="statsSplit">
        <div className="statsSection">
          <h2>Most Wins (Top 10)</h2>
          <RankedTable
            items={(stats.most_wins || []).map((p, i) => ({ rank: i + 1, ...p }))}
            columns={[
              { key: 'rank', name: '#', fieldName: 'rank', minWidth: 30, maxWidth: 30 },
              { key: 'name', name: 'Player', fieldName: 'name', minWidth: 120 },
              { key: 'wins', name: 'Wins', minWidth: 60, maxWidth: 80,
                onRender: item => fmt(item.wins) },
            ]}
          />
        </div>
        <div className="statsSection">
          <h2>Most Matches (Top 10)</h2>
          <RankedTable
            items={(stats.most_matches || []).map((p, i) => ({ rank: i + 1, ...p }))}
            columns={[
              { key: 'rank', name: '#', fieldName: 'rank', minWidth: 30, maxWidth: 30 },
              { key: 'name', name: 'Player', fieldName: 'name', minWidth: 120 },
              { key: 'matches', name: 'Matches', minWidth: 60, maxWidth: 80,
                onRender: item => fmt(item.matches) },
            ]}
          />
        </div>
      </div>

      <div className="statsSplit">
        <div className="statsSection">
          <h2>Highest Win % (Min 20 Matches)</h2>
          <RankedTable
            items={(stats.highest_win_pct || []).map((p, i) => ({ rank: i + 1, ...p }))}
            columns={[
              { key: 'rank', name: '#', fieldName: 'rank', minWidth: 30, maxWidth: 30 },
              { key: 'name', name: 'Player', fieldName: 'name', minWidth: 100 },
              { key: 'record', name: 'W-L', minWidth: 60, maxWidth: 70,
                onRender: item => `${item.wins}-${item.losses}` },
              { key: 'pct', name: 'Win %', minWidth: 55, maxWidth: 65,
                onRender: item => `${item.win_pct}%` },
            ]}
          />
        </div>
        <div className="statsSection">
          <h2>Longest Ever Win Streaks</h2>
          <RankedTable
            items={(stats.longest_streaks || []).map((p, i) => ({ rank: i + 1, ...p }))}
            columns={[
              { key: 'rank', name: '#', fieldName: 'rank', minWidth: 30, maxWidth: 30 },
              { key: 'name', name: 'Player', fieldName: 'name', minWidth: 120 },
              { key: 'streak', name: 'Streak', minWidth: 55, maxWidth: 65,
                onRender: item => fmt(item.streak) },
            ]}
          />
        </div>
      </div>

      <div className="statsSection" style={{ maxWidth: 480 }}>
        <h2>Longest Current Win Streaks</h2>
        <RankedTable
          items={(stats.current_streaks || []).map((p, i) => ({ rank: i + 1, ...p }))}
          columns={[
            { key: 'rank', name: '#', fieldName: 'rank', minWidth: 30, maxWidth: 30 },
            { key: 'name', name: 'Player', fieldName: 'name', minWidth: 120 },
            { key: 'streak', name: 'Streak', minWidth: 55, maxWidth: 65,
              onRender: item => fmt(item.streak) },
          ]}
        />
      </div>

      {/* ── Charts ────────────────────────────────────────────────── */}
      <div className="statsSection">
        <h2>Matches Per Month</h2>
        <BarChart
          data={(stats.matches_per_month || []).filter(d => d.month >= '2018')}
          indexKey="month"
          valueKey="count"
          height={320}
          formatTick={formatMonth}
          tickInterval={3}
        />
      </div>

      <div className="statsSection">
        <h2>New Players Per Month</h2>
        <BarChart
          data={(stats.new_players_per_month || []).filter(d => d.month >= '2018')}
          indexKey="month"
          valueKey="count"
          height={320}
          formatTick={formatMonth}
          tickInterval={3}
        />
      </div>

      <div className="statsSplit">
        <div className="statsSection">
          <h2>Matches Per Year</h2>
          <BarChart
            data={(stats.matches_per_year || []).filter(d => d.year >= 2018)}
            indexKey="year"
            valueKey="count"
            height={280}
          />
        </div>
        <div className="statsSection">
          <h2>New Players Per Year</h2>
          <BarChart
            data={(stats.new_players_per_year || []).filter(d => d.year >= 2018)}
            indexKey="year"
            valueKey="count"
            height={280}
          />
        </div>
      </div>
    </div>
  )
}

export default Statistics
